import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../components/Toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RatingScaleItem {
  score: number;
  label: string;
  percentage: number;
}

interface BonusLevelItem {
  key: string;
  label: string;
  factor: number;
}

interface MetricsSettings {
  ratingScale: RatingScaleItem[];
  bonusLevels: BonusLevelItem[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MetricsSettingsPage: React.FC = () => {
  const api = useApi();
  const { isAdmin } = useAuth();
  const { showToast } = useToast();
  const qc = useQueryClient();

  // Fetch current metrics config
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['metrics-settings'],
    queryFn: () => api.get('/settings/metrics') as Promise<MetricsSettings>,
  });

  // Local state for editing
  const [ratingScale, setRatingScale] = useState<RatingScaleItem[]>([]);
  const [bonusLevels, setBonusLevels] = useState<BonusLevelItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Sync local state when data loads
  useEffect(() => {
    if (metrics) {
      setRatingScale(metrics.ratingScale);
      setBonusLevels(metrics.bonusLevels);
      setHasChanges(false);
    }
  }, [metrics]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      return api.put('/settings/metrics', {
        ratingScale,
        bonusLevels,
      });
    },
    onSuccess: () => {
      showToast('Metrics settings saved successfully!', 'success');
      qc.invalidateQueries({ queryKey: ['metrics-settings'] });
      setHasChanges(false);
    },
    onError: (err: any) => {
      showToast(err?.body?.detail || 'Failed to save metrics settings', 'error');
    },
  });

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const updateRatingItem = (index: number, field: keyof RatingScaleItem, value: string | number) => {
    const updated = [...ratingScale];
    updated[index] = { ...updated[index], [field]: value };
    setRatingScale(updated);
    setHasChanges(true);
  };

  const updateBonusItem = (index: number, field: keyof BonusLevelItem, value: string | number) => {
    const updated = [...bonusLevels];
    updated[index] = { ...updated[index], [field]: value };
    setBonusLevels(updated);
    setHasChanges(true);
  };

  const addRatingItem = () => {
    const maxScore = Math.max(...ratingScale.map(r => r.score), -1);
    setRatingScale([...ratingScale, { score: maxScore + 1, label: 'New Level', percentage: 0 }]);
    setHasChanges(true);
  };

  const removeRatingItem = (index: number) => {
    setRatingScale(ratingScale.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const addBonusItem = () => {
    setBonusLevels([...bonusLevels, { key: `level_${bonusLevels.length}`, label: 'New Level', factor: 1.0 }]);
    setHasChanges(true);
  };

  const removeBonusItem = (index: number) => {
    setBonusLevels(bonusLevels.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const resetToDefaults = () => {
    if (!confirm('Reset to default values? This will discard unsaved changes.')) return;
    setRatingScale([
      { score: 0, label: 'Not Rated', percentage: 0 },
      { score: 1, label: 'Not Applicable', percentage: 0 },
      { score: 2, label: 'Not Achieved', percentage: 50 },
      { score: 3, label: 'Partially Achieved', percentage: 75 },
      { score: 4, label: 'Fully Achieved', percentage: 100 },
    ]);
    setBonusLevels([
      { key: 'normal', label: 'Normal', factor: 1.0 },
      { key: 'strong', label: 'Strong', factor: 1.25 },
      { key: 'exceptional', label: 'Exceptional', factor: 1.5 },
    ]);
    setHasChanges(true);
  };

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  };

  const thStyle: React.CSSProperties = {
    padding: '10px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #e5e7eb',
    background: '#f9fafb',
    fontWeight: 600,
  };

  const tdStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderBottom: '1px solid #e5e7eb',
    verticalAlign: 'middle',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 10px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
  };

  const numInputStyle: React.CSSProperties = {
    ...inputStyle,
    width: '80px',
    textAlign: 'right',
  };

  const btnStyle: React.CSSProperties = {
    padding: '8px 16px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    background: 'white',
    color: '#374151',
    cursor: 'pointer',
    marginRight: '8px',
  };

  const primaryBtnStyle: React.CSSProperties = {
    ...btnStyle,
    background: '#3b82f6',
    color: 'white',
    border: 'none',
  };

  const dangerBtnStyle: React.CSSProperties = {
    ...btnStyle,
    color: '#dc2626',
    border: '1px solid #dc2626',
    padding: '4px 10px',
    fontSize: '13px',
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem' }}>
        <h2>Metrics Settings</h2>
        <p style={{ color: '#6b7280' }}>Admin access required to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: '2rem' }}>
        <h2>Metrics Settings</h2>
        <p style={{ color: '#6b7280' }}>Loading configuration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem' }}>
        <h2>Metrics Settings</h2>
        <p style={{ color: '#ef4444' }}>Failed to load metrics settings.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '900px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Metrics Settings</h2>
          <p style={{ color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
            Configure rating scale and bonus level multipliers
          </p>
        </div>
        <div>
          <button style={btnStyle} onClick={resetToDefaults}>
            Reset to Defaults
          </button>
          <button
            style={{ ...primaryBtnStyle, opacity: hasChanges ? 1 : 0.5 }}
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Rating Scale Section */}
      <section style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>Rating Scale</h3>
          <button style={btnStyle} onClick={addRatingItem}>
            + Add Level
          </button>
        </div>
        <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '1rem' }}>
          Define score values, labels, and the percentage they contribute to weighted calculations.
        </p>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '80px' }}>Score</th>
                <th style={thStyle}>Label</th>
                <th style={{ ...thStyle, width: '120px' }}>Percentage</th>
                <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ratingScale.map((item, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      min={0}
                      max={10}
                      value={item.score}
                      onChange={(e) => updateRatingItem(idx, 'score', Number(e.target.value))}
                      style={numInputStyle}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => updateRatingItem(idx, 'label', e.target.value)}
                      style={inputStyle}
                    />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input
                        type="number"
                        min={0}
                        max={200}
                        step={5}
                        value={item.percentage}
                        onChange={(e) => updateRatingItem(idx, 'percentage', Number(e.target.value))}
                        style={numInputStyle}
                      />
                      <span style={{ color: '#6b7280' }}>%</span>
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      style={dangerBtnStyle}
                      onClick={() => removeRatingItem(idx)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Bonus Levels Section */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>Bonus Levels</h3>
          <button style={btnStyle} onClick={addBonusItem}>
            + Add Level
          </button>
        </div>
        <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '1rem' }}>
          Define bonus multiplier factors. E.g., Exceptional at 1.5 means 150% of base contribution.
        </p>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '150px' }}>Key</th>
                <th style={thStyle}>Label</th>
                <th style={{ ...thStyle, width: '120px' }}>Factor</th>
                <th style={{ ...thStyle, width: '100px' }}>Multiplier</th>
                <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bonusLevels.map((item, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={item.key}
                      onChange={(e) => updateBonusItem(idx, 'key', e.target.value)}
                      style={inputStyle}
                      placeholder="e.g., exceptional"
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => updateBonusItem(idx, 'label', e.target.value)}
                      style={inputStyle}
                      placeholder="e.g., Exceptional"
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      min={0}
                      max={5}
                      step={0.05}
                      value={item.factor}
                      onChange={(e) => updateBonusItem(idx, 'factor', Number(e.target.value))}
                      style={numInputStyle}
                    />
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: item.factor >= 1.5 ? '#059669' : item.factor > 1 ? '#d97706' : '#6b7280' }}>
                    {(item.factor * 100).toFixed(0)}%
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      style={dangerBtnStyle}
                      onClick={() => removeBonusItem(idx)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Formula Reference */}
      <section style={{ marginTop: '2rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '14px' }}>Weighted Contribution Formula</h4>
        <code style={{ fontSize: '13px', color: '#374151', background: '#e5e7eb', padding: '4px 8px', borderRadius: '4px' }}>
          WeightedContribution = (Score / MaxScore) × Weight × BonusFactor
        </code>
        <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '0.75rem', marginBottom: 0 }}>
          Example: Score 4 (Fully Achieved) with Exceptional bonus (1.5×) and Weight 0.25 = (4/4) × 0.25 × 1.5 = <strong>0.375</strong>
        </p>
      </section>
    </div>
  );
};

export default MetricsSettingsPage;
